import { useTranslate } from '@ur-solutions/react-hooks'

import { cn } from '@/lib/utils'
import { FAIcon } from '@/components/fa-icon/fa-icon'

import { TimelineRail } from './timeline-rail'

const SkeletonEntry = ({ isLast }: { isLast: boolean }) => (
  <div className="flex gap-3.5">
    <TimelineRail isLast={isLast}>
      <div className="h-7 w-7 animate-pulse rounded-full bg-quaternary" />
    </TimelineRail>
    <div className={cn('flex flex-1 flex-col gap-3', !isLast && 'pb-6')}>
      <div className="flex items-center justify-between">
        <div className="h-3 w-40 animate-pulse rounded bg-quaternary" />
        <div className="h-2.5 w-14 animate-pulse rounded bg-quaternary" />
      </div>
      <div className="h-14 w-full animate-pulse rounded-lg bg-quaternary" />
    </div>
  </div>
)

/** Placeholder timeline shown while the issue (and its logs) load. */
export const ActionLogSkeleton = () => (
  <div className="flex flex-col">
    {[0, 1, 2].map(i => (
      <SkeletonEntry key={i} isLast={i === 2} />
    ))}
  </div>
)

/** Shown when the action log failed to load. */
export const ActionLogError = () => {
  const t = useTranslate({
    errorTitle: 'issues.action-log.error-title',
    errorBody: 'issues.action-log.error-body',
  })

  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
        <FAIcon icon="triangle-exclamation" className="text-xl text-red-600" />
      </div>
      <span className="text-base font-semibold text-primary">
        {t.errorTitle}
      </span>
      <span className="max-w-xs text-sm text-muted-foreground">
        {t.errorBody}
      </span>
    </div>
  )
}

/** Shown when the issue has no action log entries yet. */
export const ActionLogEmpty = () => {
  const t = useTranslate({
    emptyTitle: 'issues.action-log.empty-title',
    emptyBody: 'issues.action-log.empty-body',
  })

  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-quaternary">
        <FAIcon icon="clock-rotate-left" className="text-xl text-tertiary" />
      </div>
      <span className="text-base font-semibold text-primary">
        {t.emptyTitle}
      </span>
      <span className="max-w-xs text-sm text-muted-foreground">
        {t.emptyBody}
      </span>
    </div>
  )
}
